import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import { triggerBlobDownload } from '@/utils/downloadHelper';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export default function PdfViewerModal({ url, name, onClose, blob, filename }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const onDocumentLoadSuccess = ({ numPages: total }) => {
    setNumPages(total);
    setPageNumber(1);
  };

  const handleDownload = async () => {
    if (blob) {
      // Blob is already available, download directly
      triggerBlobDownload(blob, filename || `${name || 'document'}.pdf`);
    } else if (url) {
      // URL is provided - fetch the blob from the URL
      try {
        const response = await fetch(url);
        const fetchedBlob = await response.blob();
        triggerBlobDownload(fetchedBlob, filename || `${name || 'document'}.pdf`);
      } catch (error) {
        console.error('Error fetching PDF for download:', error);
        // Fallback: try opening in new tab
        window.open(url, '_blank');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-slate-900 flex-shrink-0">
        <h3 className="text-white font-semibold text-sm">{name || 'PDF'} — Preview</h3>
        <div className="flex items-center gap-4">
          {numPages && (
            <div className="flex items-center gap-2">
              <button
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber(p => p - 1)}
                className="p-1 text-white/70 hover:text-white disabled:text-white/30 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-white/80 text-sm min-w-[80px] text-center">
                Page {pageNumber} of {numPages}
              </span>
              <button
                disabled={pageNumber >= numPages}
                onClick={() => setPageNumber(p => p + 1)}
                className="p-1 text-white/70 hover:text-white disabled:text-white/30 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
          {(blob || url) && (
            <button
              data-testid="download-pdf-btn"
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          )}
          <button
            data-testid="close-pdf-viewer"
            onClick={onClose}
            className="p-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-auto flex justify-center py-6 bg-slate-700">
        <Document
          file={blob || url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          }
          error={
            <div className="text-white text-center py-20">
              <p className="text-lg font-semibold mb-2">Failed to load PDF</p>
              <p className="text-white/60 text-sm">The document could not be rendered.</p>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={800}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
    </div>
  );
}
